// Shared list-row card for Email + Template records. Used by both list
// screens (`email-list.tsx`, `template-list.tsx`) AND the Home screen's
// Recent Emails + Recent Templates sections (P-037.31 §D — Banks's
// audit: Home rows adopt the same rich card as the list screens so
// Edit / Duplicate / Delete are reachable from the dashboard).
//
// **3rd-use threshold met.** Pre-extraction the row was duplicated
// across email-list.tsx + template-list.tsx (2 consumers); P-037.31 §D
// adds 2 more on Home for 4 total. Per [[feedback_chat_questions]]
// extract-on-third-use precedent (P-007's `FF`, P-015's `gridLayout`,
// etc.), the extraction is the right call here.
//
// **Read-only props.** The row knows nothing about which store slice
// it operates on — name + subtitle are caller-resolved (with the
// `*_PLACEHOLDER` fallback applied at the call site so this component
// doesn't import per-record constants); the action callbacks are
// caller-provided. Keeps the component testable in isolation + free
// of store coupling.

import type { JSX } from 'preact'

import { secondaryButtonClassCompact } from '../button-classes'

export interface RecordRowProps {
  /** Display name. Caller resolves the empty-name placeholder upstream
   *  so this component doesn't have to know which `*_PLACEHOLDER` to
   *  pick. */
  name: string
  /** One-line subtitle (e.g., "Template name · 4 blocks" or
   *  "No template · 4 blocks"). Caller composes per record type. */
  subtitle: string
  /** Edit-action href (e.g., `#/emails/${id}` or `#/templates/${id}`). */
  editHref: string
  /** Duplicate-action handler — fires inline (non-destructive, no
   *  confirm modal per the list-screen pattern). */
  onDuplicate: () => void
  /** Delete-action handler. The caller's `useDeleteConfirm` hook
   *  intercepts to surface the confirm modal; this row just calls
   *  whatever the caller wires up. */
  onDelete: () => void
}

export function RecordRow({
  name,
  subtitle,
  editHref,
  onDuplicate,
  onDelete,
}: RecordRowProps): JSX.Element {
  return (
    <li class="flex items-center justify-between gap-4 rounded border border-gray-200 px-3 py-2">
      <div class="min-w-0">
        <div class="truncate text-sm font-medium text-gray-900">{name}</div>
        <div class="text-xs text-gray-500">{subtitle}</div>
      </div>
      <div class="flex shrink-0 gap-2 text-sm">
        <a href={editHref} class={secondaryButtonClassCompact}>
          Edit
        </a>
        <button type="button" onClick={onDuplicate} class={secondaryButtonClassCompact}>
          Duplicate
        </button>
        <button
          type="button"
          onClick={onDelete}
          class="rounded border border-red-300 bg-white px-3 py-1 text-red-700 hover:bg-red-50"
        >
          Delete
        </button>
      </div>
    </li>
  )
}
